'use strict';

import { useMemo } from 'react';
import { flattenIngredients } from '@utils/Recipe';
import { humanizeMilliliters, humanizeGrams } from '@utils/Math';
import { useAssets } from '../../../contexts';

/**
 * Hook to calculate and format meal usage data for grocery items
 *
 * This hook processes grocery data to show which meals/recipes use each ingredient
 * and how much quantity is needed from each recipe.
 */
const useGroceryMealUsage = (groceries) => {
    // Get assets from context instead of props
    const { recipes, foods, details } = useAssets();
    
    const mealUsageData = useMemo(() => {
        if (!groceries || !Array.isArray(groceries)) {
            return {};
        }

        const usageMap = {};

        // Helper function to calculate amounts from recipe meals (adapted from original)
        const getAmountAndUnitFromRecipeMeal = (grocery, meal, recipe) => {
            if (!recipe || !details || !details[recipe.details]) {
                return { grams: 0, milliliters: 0, unit: null };
            }

            const detail = details[recipe.details];

            let grams = 0;
            let milliliters = 0;
            let amount = 1;
            let unit = null;


            // Process main recipe ingredients
            const ingredients = flattenIngredients({ detail });

            let foundMatch = false;

            ingredients.forEach(ingredient => {
                if (ingredient.food?.uuid === grocery.food_uuid) {
                    foundMatch = true;

                    if (ingredient.grams) {
                        grams += ingredient.grams * (meal.scaling || 1);
                    }
                    if (ingredient.milliliters) {
                        milliliters += ingredient.milliliters * (meal.scaling || 1);
                    }

                    // Get unit info from the food if available
                    if (!unit && ingredient.food.unit_amount) {
                        unit = {
                            amount: ingredient.food.unit_amount,
                            description: ingredient.food.unit_description,
                            grams: ingredient.food.unit_grams,
                            milliliters: ingredient.food.unit_milliliters,
                        };
                    }
                }
            });


            // Fallback unit from foods collection
            if (!unit && foods && foods[grocery.food_uuid] && foods[grocery.food_uuid].default_unit) {
                unit = Object.assign({}, foods[grocery.food_uuid].default_unit);
            }

            return { grams, milliliters, unit };
        };

        // Helper function for food meals (direct food, not recipe)
        const getAmountAndUnitFromFoodMeal = (meal, food) => {
            return {
                grams: meal.logged_grams,
                milliliters: meal.logged_milliliters,
                unit: meal.logged_unit,
                amount: meal.logged_amount,
            };
        };

        groceries.forEach(grocery => {
            if (!grocery.meals || !Array.isArray(grocery.meals)) {
                usageMap[grocery.uuid] = [];
                return;
            }

            // Sort meals by offset for logical ordering
            const sortedMeals = grocery.meals.sort((a, b) => a.offset - b.offset);

            const mealData = sortedMeals.map(meal => {
                const recipe = recipes && recipes[meal.recipe_uuid];
                const food = foods && foods[grocery.food_uuid];

                // Calculate amounts using the same logic as the original
                const amounts = meal.recipe_uuid
                    ? getAmountAndUnitFromRecipeMeal(grocery, meal, recipe)
                    : getAmountAndUnitFromFoodMeal(grocery, meal, food);

                // Format the meal display data
                return {
                    uuid: meal.uuid,
                    offset: meal.offset,
                    mealType: meal.meal_type,
                    recipeTitle: recipe ? recipe.title : (food ? food.name : 'Unknown'),
                    recipePrimaryImage: recipe ? recipe.image : null,
                    isRecipe: !!recipe,
                    isFood: !recipe && !!food,
                    scaling: meal.scaling || 1,
                    // Use calculated amounts
                    grams: amounts.grams,
                    milliliters: amounts.milliliters,
                    unit: amounts.unit,
                    // Additional meal context
                    mealTitle: meal.meal || (recipe ? recipe.title : (food ? food.name : 'Unknown Meal'))
                };
            });

            usageMap[grocery.food_uuid] = mealData;
        });
        return usageMap;
    }, [groceries, recipes, foods, details]);

    return {
        mealUsageData
    };
};

export default useGroceryMealUsage;
